import React, { useState, useEffect } from "react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import MasterDialog from "../components/MasterDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { UsersThree, Plus, Trash, Key } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Users() {
  const { meta, user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [resetting, setResetting] = useState(false);
  const load = () => api.get("/auth/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const doReset = async () => {
    if (newPw.trim().length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setResetting(true);
    try {
      await api.put(`/auth/users/${resetUser.user_id}`, { password: newPw });
      toast.success(`Password reset for ${resetUser.name}`);
      setResetUser(null); setNewPw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reset password"); }
    finally { setResetting(false); }
  };

  const changeRole = async (uid, role) => {
    try { await api.put(`/auth/users/${uid}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addUser = async (form) => {
    await api.post("/auth/users", { email: form.email, password: form.password, name: form.name, role: form.role || "technician" });
    toast.success("User created");
    load();
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently remove ${u.name} (${u.email}) from the system? Their login email and password will be deleted. This cannot be undone.`)) return;
    try {
      await api.delete(`/auth/users/${u.user_id}`);
      toast.success("User removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to remove user"); }
  };

  const fields = [
    { name: "name", label: "Full Name", required: true },
    { name: "email", label: "Email", required: true },
    { name: "password", label: "Password", required: true },
    { name: "role", label: "Role", type: "select", options: (meta?.roles || []).map((r) => ({ value: r, label: meta.role_labels[r] })), required: true },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline text-amber-500 flex items-center gap-2"><UsersThree size={14} /> Access Control</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">User Management</h1>
        </div>
        <Button data-testid="add-user-btn" onClick={() => setOpen(true)} className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">
          <Plus size={16} weight="bold" className="mr-1" /> Add User
        </Button>
      </div>

      <div className="overflow-x-auto rounded-sm border border-zinc-800">
        <table className="w-full min-w-[620px] text-sm">
          <thead className="bg-zinc-900 text-left overline text-zinc-500">
            <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {users.map((u) => (
              <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="bg-zinc-950">
                <td className="px-4 py-3 font-semibold">{u.name}</td>
                <td className="px-4 py-3 text-zinc-400">{u.email}</td>
                <td className="px-4 py-3">
                  <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                    <SelectTrigger data-testid={`role-${u.user_id}`} className="w-52 bg-zinc-900"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.roles || []).map((r) => <SelectItem key={r} value={r}>{meta.role_labels[r]}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">{u.active === false ? <span className="text-rose-400">Disabled</span> : <span className="text-emerald-400">Active</span>}</td>
                <td className="px-4 py-3 text-right">
                  {u.user_id === me?.user_id ? (
                    <span className="text-xs text-zinc-600">You</span>
                  ) : (
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" data-testid={`reset-pw-${u.user_id}`} onClick={() => { setResetUser(u); setNewPw(""); }}
                        className="text-zinc-400 hover:bg-amber-500/10 hover:text-amber-400">
                        <Key size={16} className="mr-1" /> Reset Password
                      </Button>
                      <Button variant="ghost" size="sm" data-testid={`delete-user-${u.user_id}`} onClick={() => deleteUser(u)}
                        className="text-zinc-400 hover:bg-rose-500/10 hover:text-rose-400">
                        <Trash size={16} className="mr-1" /> Remove
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-zinc-500">Signed in as {me?.email} ({me?.role}).</p>

      <MasterDialog title="Add User" fields={fields} open={open} onOpenChange={setOpen} onSubmit={addUser} submitLabel="Create User" />

      <Dialog open={!!resetUser} onOpenChange={(v) => { if (!v) { setResetUser(null); setNewPw(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle className="font-display">Reset Password</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-400">Set a new password for <span className="font-semibold text-zinc-200">{resetUser?.name}</span> ({resetUser?.email}). They'll use this to sign in from now on.</p>
          <div className="mt-2">
            <Label className="text-xs text-zinc-400">New Password</Label>
            <Input data-testid="reset-pw-input" type="text" value={newPw} placeholder="Minimum 6 characters"
              onChange={(e) => setNewPw(e.target.value)} className="mt-1 bg-zinc-950" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetUser(null); setNewPw(""); }}>Cancel</Button>
            <Button data-testid="reset-pw-save" onClick={doReset} disabled={resetting} className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">
              {resetting ? "Saving…" : "Set Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
